/**
 * F-132: /me/cities editor.
 * Choose 1 home city + multi-add service cities. PUT replaces the whole set.
 */
import { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/layout';
import { Button, Card, Icon, Input, Spinner, toast } from '@/components/atoms';
import { catalog, specialists as api } from '@/api';
import type { CityRow } from '@/api/specialists';
import type { City } from '@/types/domain';
import { logger } from '@/utils/logger';
import styles from './pages.module.css';

export function MyCitiesPage(): JSX.Element {
  const [picked, setPicked] = useState<CityRow[] | null>(null);
  const [all, setAll] = useState<City[] | null>(null);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.fetchMyCities(), catalog.fetchCities()])
      .then(([mine, list]) => {
        setPicked(mine.results);
        setAll(list.results);
      })
      .catch((err) => {
        logger.error('cities load failed', err);
        setPicked([]);
        setAll([]);
      });
  }, []);

  const persist = async (next: CityRow[]): Promise<void> => {
    setBusy(true);
    try {
      const resp = await api.setMyCities(
        next.map((c) => ({ slug: c.slug, is_primary: c.is_primary })),
      );
      setPicked(resp.results);
    } catch (err) {
      logger.error('cities save failed', err);
      toast.error('Не вдалось зберегти');
    } finally {
      setBusy(false);
    }
  };

  const add = (slug: string, name_ua: string): void => {
    if (!picked) return;
    if (picked.some((p) => p.slug === slug)) return;
    const hasPrimary = picked.some((p) => p.is_primary);
    void persist([...picked, { slug, name_ua, is_primary: !hasPrimary }]);
  };

  const remove = (slug: string): void => {
    if (!picked) return;
    void persist(picked.filter((p) => p.slug !== slug));
  };

  const makeHome = (slug: string): void => {
    if (!picked) return;
    void persist(picked.map((p) => ({ ...p, is_primary: p.slug === slug })));
  };

  const filtered = useMemo(() => {
    if (!all) return [];
    const q = query.trim().toLowerCase();
    return all.filter(
      (c) => !q || c.name_ua.toLowerCase().includes(q) || c.slug.includes(q),
    );
  }, [all, query]);

  if (picked === null || all === null) {
    return (
      <Layout title="Міста">
        <Spinner />
      </Layout>
    );
  }

  return (
    <Layout title="Міста">
      <div className={styles.list}>
        {picked.map((p) => (
          <Card key={p.slug}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ flex: 1 }}>
                {p.is_primary ? 'Дім · ' : ''}
                {p.name_ua}
              </strong>
              {!p.is_primary ? (
                <Button variant="ghost" onClick={() => makeHome(p.slug)}>
                  Зробити домашнім
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => remove(p.slug)}>
                <Icon name="x" size={16} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Input
        placeholder="Пошук міста"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginTop: 16 }}
      />

      <div className={styles.list}>
        {filtered.slice(0, 30).map((c) => (
          <Card key={c.slug} interactive onClick={() => add(c.slug, c.name_ua)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Icon name="plus" size={16} />
              <span>{c.name_ua}</span>
            </span>
          </Card>
        ))}
      </div>

      {busy ? <Spinner /> : null}
    </Layout>
  );
}
